<template>
  <div class="actions-toolbar">
    <div class="actions-toolbar__filters">
      <div
        ref="pills"
        class="actions-toolbar__pills"
        :class="{ 'is-expanded': expanded }"
      >
        <slot name="filters" />
        <button
          v-show="hiddenCount || expanded"
          type="button"
          class="pill actions-toolbar__more"
          :title="expanded ? 'Nascondi i filtri' : 'Mostra tutti i filtri'"
          @click="expanded = !expanded"
        >
          <strong>{{ expanded ? "−" : `+${hiddenCount}` }}</strong>
        </button>
      </div>
    </div>
    <div
      ref="buttons"
      class="d-flex justify-content-end actions-toolbar__buttons"
    >
      <slot />
    </div>
  </div>
</template>

<script>
// Barra delle azioni in cima alle pagine elenco: a sinistra le pillole dei
// filtri attivi (slot "filters"), a destra i bottoni.
// Regola: è primario (variant="lisaweb") solo il bottone di creazione, a
// destra; filtri, esportazioni e azioni massive sono secondari
// (btn--transparent). Vedi docs/SVILUPPO-UI.md.
//
// Le pillole stanno su una riga sola, così la tabella sotto non si sposta
// quando si filtra: quelle che non entrano vengono nascoste e contate nel
// bottone "+N". Il "+N" le mostra proseguendo la riga verso destra, sopra i
// bottoni, con il contorno sfumato; se non entrano nella pagina vanno a capo
// sopra la tabella, che non si sposta.
const GAP = 10; // come il gap di .actions-toolbar__pills
const MORE_WIDTH = 48; // bottone "+N"

export default {
  data() {
    return {
      hiddenCount: 0,
      expanded: false,
    };
  },
  mounted() {
    // requestAnimationFrame: evita "ResizeObserver loop completed" quando
    // fit() cambia il contenuto della riga.
    this.resizeObserver = new ResizeObserver(() =>
      requestAnimationFrame(() => this.fit()),
    );
    this.resizeObserver.observe(this.$refs.pills);
    // Le pillole cambiano quando si applica o si toglie un filtro.
    this.mutationObserver = new MutationObserver(() => this.fit());
    this.mutationObserver.observe(this.$refs.pills, {
      childList: true,
      subtree: true,
      characterData: true,
    });
    this.fit();
  },
  beforeDestroy() {
    this.resizeObserver.disconnect();
    this.mutationObserver.disconnect();
  },
  watch: {
    expanded(value) {
      const box = this.$refs.pills;
      const pill = box.querySelector(".pill");
      if (value && pill) {
        // La riga aperta resta centrata nella barra e può allargarsi fino al
        // bordo destro, sopra i bottoni.
        const offset = (box.clientHeight - pill.offsetHeight) / 2;
        box.style.setProperty("--first-row-offset", `${offset}px`);
        box.style.setProperty(
          "--buttons-width",
          `${this.$refs.buttons.offsetWidth + 16}px`,
        );
      }
      this.$nextTick(() => this.fit());
    },
  },
  methods: {
    fit() {
      const box = this.$refs.pills;
      if (!box) return;
      const pills = [...box.querySelectorAll(".status-filter .pill")];
      // Reset resta sempre visibile.
      const filters = pills.filter((p) => p.dataset.color !== "alert");
      const reset = pills.filter((p) => p.dataset.color === "alert");
      if (!filters.length) this.expanded = false;
      filters.forEach((p) => (p.style.display = ""));
      // Aperto: tutte visibili, il conteggio resta quello della riga chiusa.
      if (this.expanded) return;

      const width = (els) =>
        els.reduce((sum, el) => sum + el.offsetWidth + GAP, 0);
      const padding = parseFloat(getComputedStyle(box).paddingLeft) || 0;
      const available = box.clientWidth - padding - width(reset);
      let hidden = [];
      // Le pillole nascoste dal CSS della pagina non contano.
      const shown = filters.filter((p) => p.offsetWidth);
      if (width(shown) > available) {
        // Spazio per il bottone "+N".
        let room = available - MORE_WIDTH - GAP;
        for (const p of shown) {
          const w = p.offsetWidth + GAP;
          if (hidden.length || w > room) hidden.push(p);
          else room -= w;
        }
      }
      this.hiddenCount = hidden.length;
      hidden.forEach((p) => (p.style.display = "none"));
    },
  },
};
</script>

<style lang="scss" scoped>
.actions-toolbar {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-block: 12px 16px;
}

.actions-toolbar__filters {
  position: relative;
  flex: 1 1 auto;
  min-width: 0;
  align-self: stretch;
}

.actions-toolbar__pills {
  display: flex;
  align-items: center;
  gap: 10px;
  height: 100%;
  padding-left: 1.1rem;

  // Le pillole e il Reset diventano elementi di questa riga.
  :deep(.status-filter),
  :deep(.pill-wrapper) {
    display: contents;
  }

  :deep(.pill) {
    flex-shrink: 0;
  }

  :deep(.pill[data-color="alert"]) {
    order: 2;
  }

  // "+N" aperto: la riga prosegue verso destra sopra i bottoni, su uno
  // sfondo che sfuma ai bordi; se non entra nella pagina va a capo sopra la
  // tabella, che non si sposta.
  &.is-expanded {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 10;
    width: max-content;
    max-width: calc(100% + var(--buttons-width, 0px));
    height: auto;
    min-height: 100%;
    flex-wrap: wrap;
    row-gap: 10px;
    padding: var(--first-row-offset, 0) 8px var(--first-row-offset, 0) 1.1rem;
    background: var(--color-bg);
    box-shadow: 0 0 12px 10px var(--color-bg);
    border-radius: var(--radius-lg);
  }
}

.actions-toolbar__more {
  order: 1;
  flex-shrink: 0;
  border: 0;
  // Un <button> non eredita carattere e interlinea; min-height: l'altezza
  // delle pillole, che è data dalla loro X.
  font-family: inherit;
  line-height: inherit;
  min-height: 29px;
}

// Pillole e bottoni centrati sulla stessa linea orizzontale, anche quando i
// bottoni hanno altezze diverse (i menu a tendina).
.actions-toolbar__buttons {
  flex-shrink: 0;
  align-items: center;
  gap: 10px;
}
</style>
